import { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { MEDIA } from '@constants/layout';
import { DURATION, EASE } from '@constants/motion';
import { ROUTES } from '@constants/routes';
import { SHORTCUTS } from '@constants/shortcuts';
import { STORAGE } from '@constants/storage';
import { BRAND_SHORT } from '@constants/locale';
import { CONNECTION_LOST_DELAY_MS } from '@constants/timing';
import { hasPermission, usesOperations, useSession } from '@lib/auth';
import { useBodyClass, useEscapeKey, useMediaQuery, usePageEntrance } from '@lib/hooks';
import { store } from '@lib/storage';
import { isTypingTarget } from '@lib/utils/keyboard';
import { prefersReducedMotion } from '@lib/utils/motion';
import { useConnectionLost } from '@lib/stream';
import { CONNECTION } from '@locales/id/messages';
import { IconButton, LiveIndicator, RouteBoundary, SkeletonPage } from '@ui';
import { AccountMenu } from './AccountMenu';
import { IdlePrefetch } from './IdlePrefetch';
import { navGroups, pageTitle } from './nav';
import { NotificationBell } from './NotificationBell';
import { Rail } from './Rail';
import { WibClock } from './WibClock';

/**
 * Laptop shell: navy navigation rail on the left (a drawer on phones and tablets), a slim header with the
 * page title, alerts bell, live indicator, WIB clock and account menu, and the page.
 */
export function Shell({ prefetch }: { prefetch: (() => Promise<unknown>)[] }) {
  const { me, meta, logout } = useSession();
  const drawer = useMediaQuery(MEDIA.drawer);
  const [expanded, setExpanded] = useState(() => store.get<boolean>(STORAGE.railExpanded, false));
  const [open, setOpen] = useState(false);
  const rail = useRef<HTMLElement>(null);
  const menuButton = useRef<HTMLButtonElement>(null);
  const loc = useLocation();
  const groups = useMemo(() => navGroups(me), [me]);
  const title = pageTitle(loc.pathname, groups);
  const home = usesOperations(me) ? ROUTES.operations.home : ROUTES.home;

  // closing the drawer returns focus to the menu button, once the page behind it is no longer inert
  const refocus = useRef(false);
  const close = useCallback(() => {
    refocus.current = true;
    setOpen(false);
  }, []);
  useEffect(() => {
    if (open || !refocus.current) return;
    refocus.current = false;
    menuButton.current?.focus();
  }, [open]);
  const openRef = useRef(open);
  openRef.current = open;
  useEffect(() => {
    if (openRef.current) close();
  }, [loc.pathname, close]);
  useEffect(() => setOpen(false), [drawer]); // the rail is no drawer any more: nothing to return to
  useEscapeKey(close, open);
  useBodyClass('no-scroll', open);
  useEffect(() => {
    if (open) (rail.current?.querySelector<HTMLElement>('.rail-link.active') ?? rail.current?.querySelector<HTMLElement>('.rail-link'))?.focus();
  }, [open]);

  // pinning the rail open or shut moves the page by the rail's change in width: it slides there (FLIP,
  // transform only) while the rail itself widens or narrows
  const main = useRef<HTMLDivElement>(null);
  // a new page: the content area alone fades in and rises (the rail and the top bar stay still)
  const page = useRef<HTMLElement>(null);
  usePageEntrance(page);
  const leftBefore = useRef<number | null>(null);
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  const toggleExpanded = useCallback(() => {
    const next = !expandedRef.current;
    leftBefore.current = main.current?.getBoundingClientRect().left ?? null;
    store.set(STORAGE.railExpanded, next);
    setExpanded(next);
  }, []);
  useLayoutEffect(() => {
    const el = main.current;
    const before = leftBefore.current;
    leftBefore.current = null;
    if (!el || before == null || prefersReducedMotion()) return;
    const dx = before - el.getBoundingClientRect().left;
    if (dx) el.animate([{ transform: `translateX(${dx}px)` }, { transform: 'none' }], { duration: DURATION.base, easing: EASE.out });
  }, [expanded]);
  // "[" pins the rail open or shut (laptops; never while typing or while a dialog is open)
  useEffect(() => {
    if (drawer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== SHORTCUTS.toggleRail || e.ctrlKey || e.metaKey || e.altKey || e.repeat || isTypingTarget(e.target)) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return; // a dialog has the keyboard
      e.preventDefault();
      toggleExpanded();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawer, toggleExpanded]);
  const connectionLost = useConnectionLost(CONNECTION_LOST_DELAY_MS);
  return (
    <div className={`app ${!drawer && expanded ? 'rail-expanded' : ''} ${drawer ? 'has-drawer' : ''}`}>
      <a className="skip" href="#main">Lompat ke isi</a>
      <Rail ref={rail} groups={groups} appName={BRAND_SHORT} home={home} expanded={expanded} drawer={drawer} open={open} onToggleExpanded={toggleExpanded} onClose={close} />
      {drawer && open && <div className="rail-scrim" onClick={close} aria-hidden="true" />}
      {/* while the drawer is open it is modal: the page behind it cannot be reached */}
      <div ref={main} className="app-main" inert={drawer && open}>
        <header className="appbar">
          {drawer && <IconButton ref={menuButton} icon="menu" label="Buka menu" className="appbar-menu" aria-expanded={open} aria-controls="rail" onClick={() => setOpen(true)} />}
          <div className="appbar-title">
            {title?.group && <span className="appbar-group">{title.group}</span>}
            <span className="appbar-page">{title?.title ?? meta.appName}</span>
          </div>
          <div className="appbar-end">
            <WibClock />
            <LiveIndicator hideTextOnPhone />
            {/* alerts are read with alert.read (a courier or a field verifier has none: no bell asking in vain) */}
            {hasPermission(me, 'alert.read') && <NotificationBell />}
            <AccountMenu me={me} onLogout={logout} />
          </div>
        </header>
        {connectionLost && (
          <div className="connection-banner" role="status">
            <div className="connection-banner-inner">
              <span className="dot dot-amber" aria-hidden="true" />
              {CONNECTION.lost}
            </div>
          </div>
        )}
        <main ref={page} id="main" className="page" tabIndex={-1}>
          <RouteBoundary home={home}>
            <Suspense fallback={<SkeletonPage />}>
              <Outlet />
            </Suspense>
          </RouteBoundary>
        </main>
      </div>
      <IdlePrefetch loaders={prefetch} />
    </div>
  );
}
